// src/components/chat/WorkspaceSidebar.tsx
//
// Chat-page sidebar — restructured 2026-05-07 to match the approved chat
// redesign (project_chat_redesign.md). Two collapsible groups:
//   - Personas — chat agents grouped by tier (Public / Governed). Selecting
//                a persona sets the active agent for the session.
//   - Workspace — recent conversations.
//
// Branding stays Boroondara: Roboto, white surface, brand green #2e873d.
// All collapse state is in-component; React parent doesn't need to track it.

import React, { useMemo, useState } from "react";
import { mergeStyleSets } from "@fluentui/react";
import { Plus, Trash, CaretDown, MagnifyingGlass, X, PushPin } from "@phosphor-icons/react";
import { boroondaraPalette } from "../../theme/boroondaraTheme";
import type { Agent } from "../../types/agent.types";

export interface WorkspaceSession {
  id: string;
  title: string;
  agentId: string | null;
  agentName: string | null;
  lastActivity: string;
  messageCount: number;
  /** PR 5b — when true, this conversation appears in its own 'Pinned'
   *  group above the regular Recent list. Persisted to localStorage
   *  via the existing upsertSession path. */
  pinned?: boolean;
}

interface Props {
  // Sessions (existing)
  sessions: WorkspaceSession[];
  activeSessionId: string | null;
  onSelectSession: (id: string) => void;
  onNewChat: () => void;
  onDeleteSession: (id: string) => void;
  // PR 5b — toggle a session's pinned flag
  onTogglePinSession?: (id: string, pinned: boolean) => void;
  // Personas (new — added 2026-05-07)
  agents: Agent[];
  selectedAgent: Agent | null;
  onSelectAgent: (agent: Agent) => void;
  // Tier resolver — agentId → 'public' | 'governed'. Defaults to 'governed'
  // for unknown agents (safer default — visible in Governed group).
  getAgentTier?: (agentId: string) => "public" | "governed";
  // Whether the agent honors the user's finance hierarchy scope (Finance only today)
  agentHonorsHierarchy?: (agentId: string) => boolean;
}

const styles = mergeStyleSets({
  sidebar: {
    width: 264,
    minWidth: 264,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRight: "1px solid #E5E7EB",
    background: "#f5f4f1",
    overflow: "hidden",
    "@media (max-width: 768px)": { display: "none" },
  } as any,
  topRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 12px 6px",
  },
  topLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: boroondaraPalette.text,
    paddingLeft: 4,
  },
  newBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: "1px solid #D1D5DB",
    background: "#FFFFFF",
    color: boroondaraPalette.primary,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.12s",
    selectors: {
      ":hover": { borderColor: boroondaraPalette.primary, background: "#f3fcf3" },
    },
  },

  scroll: {
    flex: 1,
    overflowY: "auto",
    padding: "6px 8px 8px",
  },

  // Group / sub-group headers (collapsible)
  groupHead: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "8px 12px",
    borderRadius: 6,
    background: "transparent",
    border: 0,
    color: boroondaraPalette.text3,
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "left" as const,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
    transition: "background 0.12s",
    selectors: { ":hover": { background: "rgba(0,0,0,0.03)", color: boroondaraPalette.text2 } },
  },
  subHead: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "6px 12px",
    borderRadius: 6,
    background: "transparent",
    border: 0,
    color: boroondaraPalette.text3,
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "left" as const,
    fontSize: 11.5,
    fontWeight: 600,
    transition: "background 0.12s",
    selectors: { ":hover": { background: "rgba(0,0,0,0.03)", color: boroondaraPalette.text2 } },
  },
  headLabel: { flex: 1, minWidth: 0 },
  headMeta: {
    fontFamily: "ui-monospace, Menlo, monospace",
    fontSize: 10,
    color: "#9CA3AF",
    marginRight: 6,
  },
  chev: {
    color: "#9CA3AF",
    transition: "transform 0.18s ease",
    flexShrink: 0,
  },
  chevCollapsed: { transform: "rotate(-90deg)" },

  // Persona row
  personaItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 12px",
    borderRadius: 6,
    cursor: "pointer",
    background: "transparent",
    border: 0,
    borderLeft: "2px solid transparent",
    width: "100%",
    fontFamily: "inherit",
    color: boroondaraPalette.text,
    textAlign: "left" as const,
    transition: "background 0.12s, border-color 0.12s",
    selectors: { ":hover": { background: "rgba(0,0,0,0.04)" } },
  },
  personaItemSelected: {
    background: "#FFFFFF",
    borderLeftColor: boroondaraPalette.primary,
    selectors: { ":hover": { background: "#FFFFFF" } },
  },
  personaDot: {
    width: 8,
    height: 8,
    borderRadius: 50,
    border: `1.5px solid #9CA3AF`,
    background: "transparent",
    flexShrink: 0,
    boxSizing: "border-box" as const,
  },
  personaDotSelected: {
    border: `1.5px solid ${boroondaraPalette.primary}`,
    background: boroondaraPalette.primary,
  },
  personaName: {
    fontSize: 13.5,
    fontWeight: 500,
    color: boroondaraPalette.text,
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  personaMarker: {
    fontFamily: "ui-monospace, Menlo, monospace",
    fontSize: 9.5,
    color: "#9CA3AF",
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    flexShrink: 0,
  },
  personaMarkerSelected: { color: boroondaraPalette.primary },

  // Conversation row
  convItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 12px",
    borderRadius: 6,
    cursor: "pointer",
    background: "transparent",
    border: 0,
    width: "100%",
    fontFamily: "inherit",
    color: boroondaraPalette.text,
    textAlign: "left" as const,
    transition: "background 0.12s",
    position: "relative" as const,
    selectors: { ":hover": { background: "rgba(0,0,0,0.04)" } },
  },
  convItemActive: {
    background: "#FFFFFF",
    selectors: { ":hover": { background: "#FFFFFF" } },
  },
  convDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    flexShrink: 0,
    background: "#C0C0C0",
  },
  convContent: { flex: 1, minWidth: 0 },
  convTitle: {
    fontSize: 13,
    fontWeight: 500,
    color: boroondaraPalette.text,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  convMeta: {
    fontSize: 11,
    color: boroondaraPalette.text3,
    marginTop: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  // PR 5b — pin button. Hidden by default, fades in on row hover. When
  // a conversation IS pinned the button is permanently visible and tinted
  // brand-green so the pinned state is obvious at a glance.
  pinBtn: {
    width: 24,
    height: 24,
    borderRadius: 4,
    border: "none",
    background: "transparent",
    color: "#A0A0A0",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    opacity: 0,
    transition: "opacity 0.12s, color 0.12s, background 0.12s",
    selectors: {
      ":hover": { color: boroondaraPalette.primary, background: "rgba(46,135,61,0.08)" },
    },
  },
  pinBtnPinned: {
    opacity: 1,
    color: boroondaraPalette.primary,
  },

  deleteBtn: {
    width: 24,
    height: 24,
    borderRadius: 4,
    border: "none",
    background: "transparent",
    color: "#A0A0A0",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    opacity: 0,
    transition: "opacity 0.12s, color 0.12s, background 0.12s",
    selectors: {
      ":hover": { color: "#D83B01", background: "rgba(216,59,1,0.08)" },
    },
  },

  emptyState: {
    padding: "24px 16px",
    textAlign: "center" as const,
    color: boroondaraPalette.text3,
    fontSize: 12,
    lineHeight: "1.6",
  },

  // PR 5a — Search inside Recent
  searchWrap: {
    position: "relative" as const,
    margin: "4px 8px 8px",
  } as any,
  searchInput: {
    width: "100%",
    boxSizing: "border-box" as const,
    background: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: 6,
    padding: "6px 28px 6px 28px",
    fontSize: 12,
    fontFamily: "inherit",
    color: boroondaraPalette.text,
    outline: "none",
    transition: "border-color 0.12s, box-shadow 0.12s",
    selectors: {
      "::placeholder": { color: "#9CA3AF" },
      ":focus": {
        borderColor: boroondaraPalette.primary,
        boxShadow: `0 0 0 3px rgba(46,135,61,0.10)`,
      },
    },
  } as any,
  searchIcon: {
    position: "absolute" as const,
    left: 8,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#9CA3AF",
    pointerEvents: "none" as const,
    display: "flex",
    alignItems: "center",
  } as any,
  searchClear: {
    position: "absolute" as const,
    right: 4,
    top: "50%",
    transform: "translateY(-50%)",
    background: "transparent",
    border: 0,
    color: "#9CA3AF",
    cursor: "pointer",
    padding: 4,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    selectors: {
      ":hover": { color: boroondaraPalette.text, background: "rgba(0,0,0,0.04)" },
    },
  } as any,
  searchEmpty: {
    padding: "14px 16px",
    textAlign: "center" as const,
    color: boroondaraPalette.text3,
    fontSize: 12,
    lineHeight: "1.5",
  },
  searchEmptyAction: {
    display: "block",
    marginTop: 6,
    background: "transparent",
    border: 0,
    color: boroondaraPalette.primary,
    cursor: "pointer",
    fontSize: 12,
    fontFamily: "inherit",
    textDecoration: "underline",
    margin: "6px auto 0",
  },
});

function formatRelativeTime(isoStr: string): string {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return new Date(isoStr).toLocaleDateString();
}

export default function WorkspaceSidebar({
  sessions,
  activeSessionId,
  onSelectSession,
  onNewChat,
  onDeleteSession,
  onTogglePinSession,
  agents,
  selectedAgent,
  onSelectAgent,
  getAgentTier,
  agentHonorsHierarchy,
}: Props) {
  // Local collapse state — sidebar OPEN by default, all groups EXPANDED
  // by default. Earlier-this-week granularity is deferred to a follow-up;
  // for now Workspace is one flat list under Recent.
  const [personasOpen, setPersonasOpen] = useState(true);
  const [publicOpen, setPublicOpen] = useState(true);
  const [governedOpen, setGovernedOpen] = useState(true);
  const [pinnedOpen, setPinnedOpen] = useState(true);
  const [workspaceOpen, setWorkspaceOpen] = useState(true);

  // PR 5a — search inside Recent. PR 5b — search filters BOTH pinned and
  // recent groups; when the query is non-empty BOTH groups force open so
  // the user always sees their matches as they type.
  const [searchQuery, setSearchQuery] = useState("");
  const trimmedQuery = searchQuery.trim().toLowerCase();
  const filteredSessions = useMemo(() => {
    if (!trimmedQuery) return sessions;
    return sessions.filter(
      (s) =>
        s.title.toLowerCase().includes(trimmedQuery) ||
        (s.agentName ?? "").toLowerCase().includes(trimmedQuery),
    );
  }, [sessions, trimmedQuery]);

  // Storage already sorts by lastActivity desc, so a stable filter
  // preserves the right order in both buckets.
  const pinnedSessions = filteredSessions.filter((s) => s.pinned);
  const unpinnedSessions = filteredSessions.filter((s) => !s.pinned);

  const isSearching = trimmedQuery.length > 0;
  const pinnedExpanded = pinnedOpen || isSearching;
  const recentExpanded = workspaceOpen || isSearching;
  const hasNoSearchMatches = isSearching && filteredSessions.length === 0;

  // Resolve tier for each agent (default: governed for unknown — safer)
  const tierOf = (a: Agent): "public" | "governed" => {
    if (getAgentTier) return getAgentTier(a.id);
    return "governed";
  };
  const honorsHierarchyOf = (a: Agent): boolean => {
    if (agentHonorsHierarchy) return agentHonorsHierarchy(a.id);
    return false;
  };

  const publicAgents = agents.filter((a) => tierOf(a) === "public");
  const governedAgents = agents.filter((a) => tierOf(a) === "governed");

  // PR 5b — extracted conversation-row renderer. Used by both the Pinned
  // group and the Recent group.
  const renderConversation = (s: WorkspaceSession) => {
    const isActive = s.id === activeSessionId;
    const isPinned = !!s.pinned;
    return (
      <div
        key={s.id}
        className={`${styles.convItem} ${isActive ? styles.convItemActive : ""}`}
        onClick={() => onSelectSession(s.id)}
        onMouseEnter={(e) => {
          // Reveal pin + delete on hover. Pinned rows keep the pin button
          // visible permanently (handled by pinBtnPinned), but the delete
          // button still hover-reveals.
          const dEl = e.currentTarget.querySelector<HTMLElement>("[data-delete]");
          if (dEl) dEl.style.opacity = "1";
          const pEl = e.currentTarget.querySelector<HTMLElement>("[data-pin]:not([data-pinned='true'])");
          if (pEl) pEl.style.opacity = "1";
        }}
        onMouseLeave={(e) => {
          const dEl = e.currentTarget.querySelector<HTMLElement>("[data-delete]");
          if (dEl) dEl.style.opacity = "0";
          const pEl = e.currentTarget.querySelector<HTMLElement>("[data-pin]:not([data-pinned='true'])");
          if (pEl) pEl.style.opacity = "0";
        }}
      >
        <div
          className={styles.convDot}
          style={{ background: s.agentId ? boroondaraPalette.primary : "#C0C0C0" }}
        />
        <div className={styles.convContent}>
          <div className={styles.convTitle}>{s.title}</div>
          <div className={styles.convMeta}>
            {s.agentName || "General"} &middot; {formatRelativeTime(s.lastActivity)}
          </div>
        </div>
        {onTogglePinSession && (
          <button
            data-pin
            data-pinned={isPinned ? "true" : "false"}
            className={`${styles.pinBtn} ${isPinned ? styles.pinBtnPinned : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              onTogglePinSession(s.id, !isPinned);
            }}
            title={isPinned ? "Unpin" : "Pin to top"}
            aria-label={isPinned ? "Unpin conversation" : "Pin conversation"}
            aria-pressed={isPinned}
          >
            <PushPin size={12} weight={isPinned ? "fill" : "regular"} />
          </button>
        )}
        <button
          data-delete
          className={styles.deleteBtn}
          onClick={(e) => {
            e.stopPropagation();
            onDeleteSession(s.id);
          }}
          title="Delete"
          aria-label="Delete conversation"
        >
          <Trash size={12} weight="regular" />
        </button>
      </div>
    );
  };

  const renderPersona = (a: Agent) => {
    const isSel = selectedAgent?.id === a.id;
    const honors = honorsHierarchyOf(a);
    return (
      <button
        key={a.id}
        type="button"
        className={`${styles.personaItem} ${isSel ? styles.personaItemSelected : ""}`}
        onClick={() => onSelectAgent(a)}
      >
        <span className={`${styles.personaDot} ${isSel ? styles.personaDotSelected : ""}`} />
        <span className={styles.personaName}>{a.name}</span>
        {honors && (
          <span className={`${styles.personaMarker} ${isSel ? styles.personaMarkerSelected : ""}`}>
            scope
          </span>
        )}
      </button>
    );
  };

  return (
    <div className={styles.sidebar}>
      <div className={styles.topRow}>
        <span className={styles.topLabel}>Chat</span>
        <button className={styles.newBtn} onClick={onNewChat} title="New chat">
          <Plus size={14} weight="regular" />
        </button>
      </div>

      <div className={styles.scroll}>

        {/* PERSONAS group */}
        <button
          type="button"
          className={styles.groupHead}
          onClick={() => setPersonasOpen((v) => !v)}
          aria-expanded={personasOpen}
        >
          <span className={styles.headLabel}>Personas</span>
          <CaretDown
            size={12}
            weight="bold"
            className={`${styles.chev} ${!personasOpen ? styles.chevCollapsed : ""}`}
          />
        </button>
        {personasOpen && (
          <>
            {/* Public sub-group */}
            <button
              type="button"
              className={styles.subHead}
              onClick={() => setPublicOpen((v) => !v)}
              aria-expanded={publicOpen}
              style={{ marginLeft: 4 }}
            >
              <span className={styles.headLabel}>Public</span>
              <span className={styles.headMeta}>{publicAgents.length}</span>
              <CaretDown
                size={11}
                weight="bold"
                className={`${styles.chev} ${!publicOpen ? styles.chevCollapsed : ""}`}
              />
            </button>
            {publicOpen && (
              <div style={{ marginLeft: 4, marginBottom: 4 }}>
                {publicAgents.length === 0 ? (
                  <div className={styles.emptyState}>No public agents available.</div>
                ) : (
                  publicAgents.map(renderPersona)
                )}
              </div>
            )}

            {/* Governed sub-group */}
            <button
              type="button"
              className={styles.subHead}
              onClick={() => setGovernedOpen((v) => !v)}
              aria-expanded={governedOpen}
              style={{ marginLeft: 4 }}
            >
              <span className={styles.headLabel}>Governed</span>
              <span className={styles.headMeta}>{governedAgents.length}</span>
              <CaretDown
                size={11}
                weight="bold"
                className={`${styles.chev} ${!governedOpen ? styles.chevCollapsed : ""}`}
              />
            </button>
            {governedOpen && (
              <div style={{ marginLeft: 4, marginBottom: 6 }}>
                {governedAgents.length === 0 ? (
                  <div className={styles.emptyState}>No governed agents granted.</div>
                ) : (
                  governedAgents.map(renderPersona)
                )}
              </div>
            )}
          </>
        )}

        {/* PR 5b — search input lives at the top of the workspace area so
            it filters BOTH the Pinned and Recent groups. Only render when
            there are any conversations to search. */}
        {sessions.length > 0 && (
          <div className={styles.searchWrap} style={{ marginTop: 8 }}>
            <span className={styles.searchIcon}>
              <MagnifyingGlass size={13} weight="regular" />
            </span>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search conversations…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setSearchQuery("");
              }}
              aria-label="Search conversations"
            />
            {searchQuery && (
              <button
                type="button"
                className={styles.searchClear}
                onClick={() => setSearchQuery("")}
                title="Clear search"
                aria-label="Clear search"
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>
        )}

        {/* Empty state when there are zero matches across both groups. */}
        {hasNoSearchMatches && (
          <div className={styles.searchEmpty}>
            No conversations match "{searchQuery}".
            <button
              type="button"
              className={styles.searchEmptyAction}
              onClick={() => setSearchQuery("")}
            >
              Clear search
            </button>
          </div>
        )}

        {/* PINNED group — only render when there's something pinned (or
            something pinned matched the search). */}
        {pinnedSessions.length > 0 && (
          <>
            <button
              type="button"
              className={styles.groupHead}
              onClick={() => setPinnedOpen((v) => !v)}
              aria-expanded={pinnedExpanded}
              style={{ marginTop: 4 }}
            >
              <PushPin size={12} weight="fill" style={{ color: boroondaraPalette.primary, marginRight: 6 }} />
              <span className={styles.headLabel}>Pinned</span>
              <span className={styles.headMeta}>{pinnedSessions.length}</span>
              <CaretDown
                size={12}
                weight="bold"
                className={`${styles.chev} ${!pinnedExpanded ? styles.chevCollapsed : ""}`}
              />
            </button>
            {pinnedExpanded && (
              <div>{pinnedSessions.map(renderConversation)}</div>
            )}
          </>
        )}

        {/* RECENT group — always renders (shows the empty state when there
            are zero conversations and no search active). */}
        <button
          type="button"
          className={styles.groupHead}
          onClick={() => setWorkspaceOpen((v) => !v)}
          aria-expanded={recentExpanded}
          style={{ marginTop: 8 }}
        >
          <span className={styles.headLabel}>Recent</span>
          <span className={styles.headMeta}>
            {trimmedQuery
              ? `${unpinnedSessions.length} of ${sessions.filter((s) => !s.pinned).length}`
              : unpinnedSessions.length}
          </span>
          <CaretDown
            size={12}
            weight="bold"
            className={`${styles.chev} ${!recentExpanded ? styles.chevCollapsed : ""}`}
          />
        </button>
        {recentExpanded && (
          <div>
            {sessions.length === 0 ? (
              <div className={styles.emptyState}>
                No conversations yet.
                <br />
                Start a new chat to begin.
              </div>
            ) : unpinnedSessions.length === 0 && !isSearching ? (
              // All conversations are pinned — no unpinned to show.
              <div className={styles.emptyState}>
                All your conversations are pinned above.
              </div>
            ) : (
              unpinnedSessions.map(renderConversation)
            )}
          </div>
        )}
      </div>
    </div>
  );
}
